import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { addMonths, format, subMonths } from 'date-fns';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import logCherry from '../../../assets/logo-cherry-color.webp';
import {
  listProPayouts,
  type ProPayoutRow,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

const formatMoney = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

export default function ProEarningsPage() {
  // Currently-selected month. Drives the date filter on the payout
  // listing — defaults to "this month" instead of the legacy
  // hard-coded "March 2026".
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [payouts, setPayouts] = useState<ProPayoutRow[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  // Load lifetime payouts once; we filter to the picked month on the
  // client. Most pros have well under the 200-row page size below.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listProPayouts({ limit: 200 })
      .then((res) => {
        if (cancelled) return;
        setPayouts(res.data ?? []);
        setTotals(res.totals_by_status ?? {});
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = apiErrorMessage(err, 'Could not load earnings.');
          toast.error(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePrevMonth = () => setCurrentDate((prev) => subMonths(prev, 1));
  const handleNextMonth = () => setCurrentDate((prev) => addMonths(prev, 1));

  const monthStart = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  ).getTime();
  const monthEnd = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    1,
  ).getTime();

  // Payouts that fall within the picked month — keyed off
  // transferred_at when available (the actual money-out date),
  // otherwise the row's created_at.
  const monthPayouts = useMemo(() => {
    return payouts.filter((p) => {
      const ts = new Date(p.transferred_at ?? p.created_at).getTime();
      return ts >= monthStart && ts < monthEnd;
    });
  }, [payouts, monthStart, monthEnd]);

  const currency = payouts[0]?.currency ?? 'GBP';

  const monthGross = useMemo(
    () =>
      monthPayouts.reduce((acc, p) => acc + Number(p.gross_amount ?? 0), 0),
    [monthPayouts],
  );
  const monthFees = useMemo(
    () =>
      monthPayouts.reduce(
        (acc, p) => acc + Number(p.platform_fee_amount ?? 0),
        0,
      ),
    [monthPayouts],
  );
  const monthNet = useMemo(
    () => monthPayouts.reduce((acc, p) => acc + Number(p.net_amount ?? 0), 0),
    [monthPayouts],
  );

  // "Wallet balance" — proxy with lifetime PAID total minus whatever
  // we've shown is still pending. Stripe Connect doesn't expose an
  // available-balance via our public payout rows yet; this matches
  // the customer expectation of "money already wired to me".
  const lifetimePaid = totals.PAID ?? 0;

  // "Next cycle payment date" — Stripe handles the cadence and we
  // don't have an upcoming-payout API yet. Best honest proxy: the
  // earliest PENDING transfer.
  const nextPendingDate = useMemo(() => {
    const pending = payouts
      .filter((p) => p.status === 'PENDING')
      .map((p) => p.created_at)
      .filter(Boolean)
      .sort();
    return pending[0] ?? null;
  }, [payouts]);

  const handleDownloadCSV = () => {
    const header = [
      'Reference',
      'Date',
      'Status',
      'Gross',
      'Platform fee',
      'Net',
      'Currency',
    ];
    const rows = monthPayouts.map((p) => [
      p.booking?.reference ?? '',
      p.transferred_at ?? p.created_at,
      p.status,
      Number(p.gross_amount ?? 0).toFixed(2),
      Number(p.platform_fee_amount ?? 0).toFixed(2),
      Number(p.net_amount ?? 0).toFixed(2),
      p.currency,
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `earnings_${format(currentDate, 'MMMM_yyyy')}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#fff] flex flex-col font-['DM_Sans',sans-serif]">
      <HeaderPro />

      <main className="flex-1 w-full max-w-[1140px] mx-auto px-4 py-6 md:py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6 md:gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/professional/account-settings"
              className="w-10 h-10 flex items-center justify-center bg-white rounded-lg border border-[#eee] text-[#3a3335] hover:bg-[#f2eee6] transition-colors shrink-0"
            >
              <span className="material-icons text-[20px]">chevron_left</span>
            </Link>
            <div>
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px]">
                My Earnings
              </h1>
              <p className="text-[#908e8e] text-[14px]">
                Your earnings overview
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg border border-[#eee] h-10">
              <span className="material-icons text-[18px] text-[#908e8e]">
                calendar_today
              </span>
              <span className="text-[14px] font-bold text-[#3a3335] whitespace-nowrap">
                {format(currentDate, 'MMMM yyyy')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevMonth}
                className="w-10 h-10 flex items-center justify-center bg-white rounded-lg border border-[#eee] text-[#3a3335] hover:bg-[#f2eee6] transition-colors"
                aria-label="Previous month"
              >
                <span className="material-icons text-[20px]">
                  chevron_left
                </span>
              </button>
              <button
                onClick={handleNextMonth}
                className="w-10 h-10 flex items-center justify-center bg-white rounded-lg border border-[#eee] text-[#3a3335] hover:bg-[#f2eee6] transition-colors"
                aria-label="Next month"
              >
                <span className="material-icons text-[20px]">
                  chevron_right
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-[#f2eee6] rounded-lg p-8 flex items-center justify-between relative overflow-hidden py-5">
            <div>
              <p className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[20px] mb-2">
                Lifetime paid
              </p>
              <p className="text-[#908e8e] text-[14px] mb-4">
                Already wired to your bank
              </p>
              <p className="font-extrabold text-[#3a3335] text-[28px]">
                {loading ? '—' : formatMoney(lifetimePaid, currency)}
              </p>
            </div>
            <div className="flex flex-col items-center opacity-80">
              <img
                src={logCherry}
                alt="Cherry Picked"
                className="w-[130px] h-auto mb-1"
              />
            </div>
          </div>

          <div className="bg-[#f2eee6] rounded-lg p-8 py-5">
            <p className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[20px] mb-2">
              Next pending payout
            </p>
            <p className="text-[#908e8e] text-[14px] mb-4">
              Stripe runs payouts on its own schedule
            </p>
            <p className="font-extrabold text-[#3a3335] text-[28px]">
              {nextPendingDate ? formatDate(nextPendingDate) : '—'}
            </p>
          </div>
        </div>

        {/* History */}
        <div className="mb-6">
          <p className="font-bold text-[#3a3335] text-[15px] mb-6">
            {format(currentDate, 'MMMM yyyy')} summary
          </p>

          <div className="bg-white rounded-lg border border-[#eee] p-10">
            <div className="flex justify-between items-start mb-8">
              <div>
                <p className="font-extrabold text-[#8e2a2f] text-[28px] mb-2">
                  {loading ? '—' : formatMoney(monthNet, currency)}
                </p>
                <p className="text-[#908e8e] text-[14px] mb-6">
                  Your earnings after fees.
                </p>

                <div className="space-y-1">
                  <p className="text-[#3a3335] font-bold text-[14px]">
                    Total billed:{' '}
                    {loading ? '—' : formatMoney(monthGross, currency)}
                  </p>
                  <p className="text-[#3a3335] font-bold text-[14px]">
                    Total fees:{' '}
                    {loading ? '—' : formatMoney(monthFees, currency)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleDownloadCSV}
                disabled={monthPayouts.length === 0}
                className="px-4 h-[25px] flex items-center justify-center bg-[#8e2a2f] text-white rounded-lg font-bold text-[10px] hover:bg-[#7a2428] transition-all disabled:opacity-50"
              >
                Download CSV
              </button>
            </div>

            <div className="h-[1px] bg-[#f5f5f5] w-full mb-8" />

            {loading ? (
              <p className="text-sm text-[#908e8e]">Loading earnings…</p>
            ) : monthPayouts.length === 0 ? (
              <p className="text-sm text-[#908e8e]">
                No payouts in {format(currentDate, 'MMMM yyyy')}.
              </p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="text-[#908e8e] text-[12px] uppercase tracking-wide">
                  <tr>
                    <th className="py-2 font-medium">Booking</th>
                    <th className="py-2 font-medium">Date</th>
                    <th className="py-2 font-medium text-right">Gross</th>
                    <th className="py-2 font-medium text-right">Fee</th>
                    <th className="py-2 font-medium text-right">Net</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {monthPayouts.map((p) => (
                    <tr key={p.id} className="border-t border-[#ede8e9]">
                      <td className="py-2 font-mono text-[12px]">
                        {p.booking?.reference ?? '—'}
                      </td>
                      <td className="py-2 text-[12px] text-[#908e8e]">
                        {formatDate(p.transferred_at ?? p.created_at)}
                      </td>
                      <td className="py-2 text-right">
                        {formatMoney(Number(p.gross_amount), p.currency)}
                      </td>
                      <td className="py-2 text-right text-[#908e8e]">
                        −{' '}
                        {formatMoney(
                          Number(p.platform_fee_amount),
                          p.currency,
                        )}
                      </td>
                      <td className="py-2 text-right font-bold">
                        {formatMoney(Number(p.net_amount), p.currency)}
                      </td>
                      <td className="py-2 text-[12px]">{p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
